import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Attachment, FileGenerationStatus } from '../types/chat';
import { Download, Loader2 } from 'lucide-react';
import { downloadFileById, getFileText } from 'services/storage-service';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { useTheme } from 'components/core/theme-provider';
import { Button } from 'components/ui/button';
import { isDashboardAttachment } from '../utils/assistant-file-preview';
import { downloadDashboard } from '../utils/dashboard-export';

type DownloadAssistantFileProps = {
  file: Attachment;
  variant?: 'icon' | 'labeled';
};

export const DownloadAssistantFile = ({ file, variant = 'icon' }: DownloadAssistantFileProps) => {
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();
  const { theme } = useTheme();
  const { toast } = useToast();
  const isDashboard = isDashboardAttachment(file.fileName, file.extension);

  const downloadFile = async (fileId: string) => {
    await downloadFileById(fileId, file.fileName, configurationSettings, undefined);
  };

  const downloadDashboardZip = async () => {
    try {
      const source = await getFileText(file.fileId, configurationSettings, undefined);
      if (!source) throw new Error('Dashboard file is not available');
      await downloadDashboard(source);
    } catch {
      toast({
        variant: 'destructive',
        title: t('DOWNLOAD_FAILED', { defaultValue: 'Download failed' }),
        description: t('DASHBOARD_DOWNLOAD_FAILED_DESCRIPTION', {
          defaultValue: 'Unable to download this dashboard. It may no longer be available.',
        }),
      });
    }
  };

  if (
    file.sender === 'assistant' &&
    file.status &&
    file.status === FileGenerationStatus.IN_PROGRESS
  ) {
    return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
  }

  // Dashboards download as a ZIP export; other files download as stored.
  const onClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (isDashboard) downloadDashboardZip();
    else downloadFile(file.fileId);
  };
  const title = isDashboard
    ? t('DOWNLOAD_AS_ZIP', { defaultValue: 'Download as ZIP' })
    : t('DOWNLOAD_FILE', { defaultValue: 'Download file' });

  return variant === 'labeled' ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      title={title}
      className="h-8 min-w-[5.75rem] shrink-0 rounded-lg border-border bg-background pl-4 pr-3 text-sm font-medium text-foreground shadow-none hover:bg-muted"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={onClick}
    >
      {t('DOWNLOAD', { defaultValue: 'Download' })}
    </Button>
  ) : (
    <button
      type="button"
      title={title}
      className="flex h-8 w-8 items-center justify-center rounded-full p-1 text-gray-500 hover:bg-muted dark:text-gray-400"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={onClick}
    >
      <Download className={`h-4 w-4 ${theme === 'dark' ? 'text-true-white' : 'text-[#3D3D3D]'}`} />
    </button>
  );
};
